import { useState, useEffect, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ChevronLeft, Plus, Pencil, Trash2, Wand2, X, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { useFb } from "../../utils/football";
import { useAuth } from "../../contexts/AuthContext";

const fmtDay = (md) => {
  try { return new Date(`${md}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" }); }
  catch { return md; }
};
const daysSummary = (mds) => {
  const arr = mds || [];
  if (arr.length === 0) return "No match days";
  return `${arr.length} match day${arr.length > 1 ? "s" : ""}: ${arr.map(fmtDay).join(", ")}`;
};

// ---- Add / Edit week modal ----
function WeekModal({ week, days, weeks, onClose, onSaved }) {
  const fb = useFb();
  const editing = !!week?.id;
  const [name, setName] = useState(week?.week_name || "");
  const [order, setOrder] = useState(
    week?.display_order ?? ((weeks || []).reduce((m, w) => Math.max(m, w.display_order || 0), 0) + 1)
  );
  const [selected, setSelected] = useState(new Set(week?.match_days || []));
  const [saving, setSaving] = useState(false);

  // Which other week (if any) already owns a given day → warn (not block).
  const ownerOf = (md) => (weeks || []).find((w) => w.id !== week?.id && (w.match_days || []).includes(md));

  const toggle = (md) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(md) ? n.delete(md) : n.add(md);
      return n;
    });
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Week name is required"); return; }
    setSaving(true);
    try {
      await fb.post("/football/admin/weeks", {
        id: week?.id, week_name: name.trim(), match_days: [...selected],
        display_order: Number(order) || 0, is_active: true,
      });
      toast.success(editing ? "Week updated" : "Week created");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save week");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/75 p-0 backdrop-blur-sm sm:items-center sm:p-4" data-testid="week-modal">
      <div className="max-h-[88vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-amber-500/30 bg-slate-800 p-5 sm:rounded-2xl">
        <div className="mb-3 flex items-start justify-between">
          <h2 className="text-lg font-extrabold text-white">{editing ? "Edit Week" : "Add Week"}</h2>
          <button onClick={onClose} className="rounded-full p-1 text-slate-400 hover:text-white" data-testid="week-modal-close"><X size={20} /></button>
        </div>

        <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-400">Week name</label>
        <input
          value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Group — Week 1"
          className="w-full rounded-lg border border-border bg-slate-900 px-3 py-2 text-sm text-white outline-none focus:border-amber-500"
          data-testid="week-name-input"
        />

        <label className="mb-1 mt-3 block text-xs font-bold uppercase tracking-wide text-slate-400">Display order</label>
        <input
          type="number" value={order} onChange={(e) => setOrder(e.target.value)}
          className="w-28 rounded-lg border border-border bg-slate-900 px-3 py-2 text-sm text-white outline-none focus:border-amber-500"
          data-testid="week-order-input"
        />

        <label className="mb-1 mt-3 block text-xs font-bold uppercase tracking-wide text-slate-400">Match days</label>
        <div className="space-y-1.5" data-testid="week-day-selector">
          {(days || []).map((d) => {
            const owner = ownerOf(d.match_day);
            const checked = selected.has(d.match_day);
            return (
              <button
                key={d.match_day}
                onClick={() => toggle(d.match_day)}
                className={`flex w-full items-start gap-2.5 rounded-lg border px-3 py-2 text-left ${checked ? "border-amber-400 bg-amber-400/10" : "border-border bg-slate-900/50"}`}
                data-testid={`week-day-${d.match_day}`}
              >
                <span className={`mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border ${checked ? "border-amber-400 bg-amber-400 text-slate-900" : "border-slate-600"}`}>
                  {checked && <span className="text-[10px] font-extrabold">✓</span>}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-bold text-white">{fmtDay(d.match_day)}</span>
                    {owner && <span className="rounded bg-red-500/20 px-1.5 py-0.5 text-[9px] font-bold text-red-300" data-testid={`week-day-conflict-${d.match_day}`}>in {owner.week_name}</span>}
                  </span>
                  <span className="block truncate text-[11px] text-slate-400">
                    {(d.fixtures || []).map((f) => `${f.home || "TBD"} vs ${f.away || "TBD"}`).join(", ") || "No fixtures"}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex gap-2">
          <button onClick={onClose} className="flex-1 rounded-xl border border-slate-600 py-2.5 text-sm font-bold text-slate-300" data-testid="week-modal-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="flex-1 rounded-xl wc-bg-gold py-2.5 text-sm font-extrabold text-slate-900 disabled:opacity-50" data-testid="week-modal-save">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

// ---- Suggest weeks modal ----
function SuggestModal({ data, onClose, onSavedAll }) {
  const fb = useFb();
  const [rows, setRows] = useState(
    (data.suggestions || []).map((s) => ({ ...s, include: true }))
  );
  const [saving, setSaving] = useState(false);

  const update = (i, patch) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const saveAll = async () => {
    const chosen = rows.filter((r) => r.include);
    if (chosen.length === 0) { toast.error("Select at least one week"); return; }
    setSaving(true);
    try {
      for (const r of chosen) {
        await fb.post("/football/admin/weeks", {
          week_name: r.week_name.trim(), match_days: r.match_days,
          display_order: r.display_order, is_active: true,
        });
      }
      toast.success(`${chosen.length} week(s) created`);
      onSavedAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save suggestions");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/75 p-0 backdrop-blur-sm sm:items-center sm:p-4" data-testid="suggest-modal">
      <div className="max-h-[88vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-amber-500/30 bg-slate-800 p-5 sm:rounded-2xl">
        <div className="mb-3 flex items-start justify-between">
          <h2 className="text-lg font-extrabold text-white">Review Suggestions</h2>
          <button onClick={onClose} className="rounded-full p-1 text-slate-400 hover:text-white" data-testid="suggest-modal-close"><X size={20} /></button>
        </div>

        {!data.stage_field_usable ? (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="suggest-unusable">
            <p>{data.diagnostic_note}</p>
            <p className="mt-2 font-semibold">Manual week creation will still work.</p>
          </div>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400" data-testid="suggest-empty">No suggestions could be derived.</p>
        ) : (
          <>
            <p className="mb-2 text-[11px] text-slate-500" data-testid="suggest-note">{data.diagnostic_note}</p>
            <div className="space-y-2">
              {rows.map((r, i) => (
                <div key={i} className={`rounded-xl border p-3 ${r.include ? "border-amber-400/50 bg-amber-400/5" : "border-border bg-slate-900/50"}`} data-testid={`suggest-row-${i}`}>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => update(i, { include: !r.include })}
                      className={`grid h-5 w-5 shrink-0 place-items-center rounded border ${r.include ? "border-amber-400 bg-amber-400 text-slate-900" : "border-slate-600"}`}
                      data-testid={`suggest-toggle-${i}`}
                    >
                      {r.include && <span className="text-[11px] font-extrabold">✓</span>}
                    </button>
                    <input
                      value={r.week_name}
                      onChange={(e) => update(i, { week_name: e.target.value })}
                      className="min-w-0 flex-1 rounded-lg border border-border bg-slate-900 px-2.5 py-1.5 text-sm font-bold text-white outline-none focus:border-amber-500"
                      data-testid={`suggest-name-${i}`}
                    />
                  </div>
                  <p className="mt-1.5 pl-7 text-[11px] text-slate-400">{daysSummary(r.match_days)}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={onClose} className="flex-1 rounded-xl border border-slate-600 py-2.5 text-sm font-bold text-slate-300" data-testid="suggest-cancel">Cancel</button>
              <button onClick={saveAll} disabled={saving} className="flex-1 rounded-xl wc-bg-gold py-2.5 text-sm font-extrabold text-slate-900 disabled:opacity-50" data-testid="suggest-save-all">{saving ? "Saving…" : "Save All"}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminFootballWeeksPage() {
  const fb = useFb();
  const navigate = useNavigate();
  const { dbUser } = useAuth();
  const [data, setData] = useState({ weeks: [], unassigned: [], days: [] });
  const [loading, setLoading] = useState(true);
  const [editWeek, setEditWeek] = useState(null); // {} for new, week obj for edit, null = closed
  const [suggest, setSuggest] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await fb.get("/football/admin/weeks")); }
    catch { setData({ weeks: [], unassigned: [], days: [] }); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dbUser) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbUser]);

  const del = async (w) => {
    if (!window.confirm(`Delete "${w.week_name}"? This soft-deletes it (rankings unaffected).`)) return;
    try { await fb.del(`/football/admin/weeks/${w.id}`); toast.success("Week deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const openSuggest = async () => {
    try { setSuggest(await fb.post("/football/admin/weeks/suggest", {})); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load suggestions"); }
  };

  const openAddForDay = (md) => setEditWeek({ match_days: [md] });

  if (dbUser && !dbUser.is_admin) return <Navigate to="/" replace />;

  return (
    <div className="football-theme min-h-screen bg-background pb-12" data-testid="admin-football-weeks-page">
      <div className="wc-hero-gradient px-4 pb-5 pt-4">
        <button onClick={() => navigate("/admin")} className="mb-2 inline-flex items-center gap-1 text-sm text-white/80" data-testid="admin-weeks-back"><ChevronLeft size={18} /> Admin</button>
        <h1 className="text-xl font-extrabold text-white">Football Weekly Leaderboards</h1>
        <p className="mt-1 text-sm text-white/70">Group match days into weeks for the weekly leaderboard dropdown.</p>
      </div>

      <div className="mx-auto max-w-md px-4">
        <div className="mt-4 flex gap-2">
          <button onClick={() => setEditWeek({})} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl wc-bg-gold py-2.5 text-sm font-extrabold text-slate-900" data-testid="add-week-btn">
            <Plus size={16} /> Add Week
          </button>
          <button onClick={openSuggest} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-amber-400/50 bg-amber-400/10 py-2.5 text-sm font-extrabold text-amber-200" data-testid="suggest-weeks-btn">
            <Wand2 size={16} /> Suggest Weeks
          </button>
        </div>

        {loading ? <div className="mt-5 h-40 animate-pulse rounded-xl bg-secondary/50" /> : (
          <>
            <div className="mt-5 space-y-2" data-testid="weeks-list">
              {data.weeks.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground" data-testid="weeks-empty">No weeks yet. Add one manually or use Suggest Weeks.</p>
              ) : data.weeks.map((w) => (
                <div key={w.id} className="rounded-xl border border-border bg-card p-3" data-testid={`week-row-${w.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-extrabold text-foreground">{w.week_name}</p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">{daysSummary(w.match_days)}</p>
                    </div>
                    <div className="flex shrink-0 gap-1.5">
                      <button onClick={() => setEditWeek(w)} className="grid h-8 w-8 place-items-center rounded-lg border border-border bg-secondary text-amber-300" data-testid={`week-edit-${w.id}`}><Pencil size={15} /></button>
                      <button onClick={() => del(w)} className="grid h-8 w-8 place-items-center rounded-lg border border-red-500/40 bg-red-500/10 text-red-300" data-testid={`week-delete-${w.id}`}><Trash2 size={15} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Unassigned days (read-only; tap to start a week pre-filled with that day) */}
            <div className="mt-6" data-testid="unassigned-section">
              <h2 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                <CalendarDays size={13} className="wc-gold" /> Unassigned match days
              </h2>
              {data.unassigned.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="unassigned-empty">All match days are assigned to a week.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {data.unassigned.map((md) => (
                    <button key={md} onClick={() => openAddForDay(md)} className="rounded-lg border border-amber-400/30 bg-amber-400/5 px-3 py-1.5 text-xs font-bold text-amber-200" data-testid={`unassigned-day-${md}`}>
                      + {fmtDay(md)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {editWeek && (
        <WeekModal
          week={editWeek}
          days={data.days}
          weeks={data.weeks}
          onClose={() => setEditWeek(null)}
          onSaved={() => { setEditWeek(null); load(); }}
        />
      )}
      {suggest && (
        <SuggestModal data={suggest} onClose={() => setSuggest(null)} onSavedAll={() => { setSuggest(null); load(); }} />
      )}
    </div>
  );
}
